/*
 * Guided offer / question card.
 *
 * Deliberately plain: no framework, no build step, and every control is a real
 * button, label or input so the browser keeps doing focus, labelling and touch
 * targets. The steps all exist in the DOM; this only toggles `hidden`.
 */
(function () {
  'use strict';

  var COPY = {
    long_shot: {
      title: 'Long shot',
      body: "Not likely to be accepted, but it's worth a shot."
    },
    middle: {
      title: 'Middle of the pack',
      body: "A fair offer that's in the running."
    },
    solid: {
      title: 'Solid offer',
      body: 'Your chances are good.'
    }
  };

  var EMPTY_BODY = 'Type an amount to see where it lands.';

  function digitsOnly(s) {
    return String(s == null ? '' : s).replace(/[^\d]/g, '');
  }

  function withCommas(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function emailLooksValid(v) {
    return /^[^\s@,;:<>()[\]\\]+@[^\s@.,;:<>()[\]\\]+(\.[^\s@.,;:<>()[\]\\]+)+$/.test(v);
  }

  /*
   * Piecewise: each band owns exactly one third of the bar, whatever its real
   * width in dollars. That is the point - it shows which band you are in, not
   * a true-to-scale position, so a narrow "middle" band is not a sliver.
   */
  function markerPercent(amount, lo, mid, high, hi) {
    if (!(amount > 0)) return null;
    if (amount <= lo) return 3;
    if (amount >= hi) return 97;

    var p;
    if (amount < mid) {
      p = mid > lo ? ((amount - lo) / (mid - lo)) * 33.3 : 16;
    } else if (amount < high) {
      p = high > mid ? 33.3 + ((amount - mid) / (high - mid)) * 33.4 : 50;
    } else {
      p = hi > high ? 66.7 + ((amount - high) / (hi - high)) * 33.3 : 83;
    }
    return Math.max(3, Math.min(97, p));
  }

  function bandOf(amount, mid, high) {
    if (amount < mid) return 'long_shot';
    if (amount < high) return 'middle';
    return 'solid';
  }

  function segmentOf(band) {
    return band === 'long_shot' ? 1 : band === 'middle' ? 2 : 3;
  }

  function init(card) {
    var form = card.querySelector('[data-offer-form]');
    if (!form) return;

    var endpoint = card.dataset.endpoint;
    var siteKey = card.dataset.siteKey;
    var slot = card.dataset.slot;
    var canOffer = card.dataset.canOffer === 'true';

    var lo = parseInt(card.dataset.rangeLow, 10);
    var hi = parseInt(card.dataset.rangeHigh, 10);
    var mid = parseInt(card.dataset.tierMid, 10);
    var high = parseInt(card.dataset.tierHigh, 10);

    var els = {
      steps: card.querySelectorAll('[data-step]'),
      progress: card.querySelector('[data-offer-progress]'),
      amount: card.querySelector('[data-offer-amount]'),
      marker: card.querySelector('[data-offer-marker]'),
      segs: card.querySelectorAll('[data-seg]'),
      segLabels: card.querySelectorAll('[data-seg-label]'),
      calloutTitle: card.querySelector('[data-callout-title]'),
      calloutBody: card.querySelector('[data-callout-body]'),
      name: card.querySelector('[data-offer-name]'),
      email: card.querySelector('[data-offer-email]'),
      phone: card.querySelector('[data-offer-phone]'),
      message: card.querySelector('[data-offer-message]'),
      messageOptional: card.querySelector('[data-message-optional]'),
      phoneOptional: card.querySelector('[data-phone-optional]'),
      choices: card.querySelector('[data-offer-choices]'),
      payment: card.querySelectorAll('[data-offer-payment]'),
      delivery: card.querySelectorAll('[data-offer-delivery]'),
      consent: card.querySelector('[data-offer-consent]'),
      consentRow: card.querySelector('[data-offer-consent-row]'),
      hp: card.querySelector('[data-offer-hp]'),
      turnstile: card.querySelector('[data-offer-turnstile]'),
      summary: card.querySelector('[data-offer-summary]'),
      submit: card.querySelector('[data-offer-submit]'),
      submitLabel: card.querySelector('[data-submit-label]'),
      status: card.querySelector('[data-offer-status]'),
      verifyNote: card.querySelector('[data-verify-note]'),
      tokenInput: card.querySelector('[data-offer-token]'),
      done: card.querySelector('[data-offer-done]'),
      doneTitle: card.querySelector('[data-done-title]'),
      reference: card.querySelector('[data-offer-reference]'),
      tabs: card.querySelectorAll('[data-offer-mode]')
    };

    var state = {
      mode: canOffer ? 'offer' : 'question',
      step: canOffer ? 1 : 2,
      sending: false,
      widgetId: null,
      turnstileBroken: false,
      // Bounded, because reset() re-runs the challenge: a widget that
      // times out then resets then times out again is an infinite loop.
      resets: 0,
      /*
       * The token is kept HERE, captured from the success callback, and it is
       * what submit() reads. getResponse() was the old source and it is not
       * reliable for this card: with an invisible widget the token arrives
       * asynchronously some time after render, so a read taken when the user
       * happens to click returns an empty string and the submission is
       * refused even though the challenge passed.
       */
      token: ''
    };

    /*
     * Card-level status, outside the steps.
     *
     * The previous version wrote submit errors into an element inside step 3
     * and then navigated back to step 2, which hid the message it had just
     * written. Nothing is ever reported from a place the user cannot see now.
     */
    function setStatus(msg, detail) {
      if (detail) console.error('[offer-card] ' + msg, detail);
      if (!els.status) return;
      if (msg) {
        els.status.textContent = msg;
        els.status.hidden = false;
      } else {
        els.status.textContent = '';
        els.status.hidden = true;
      }
    }

    // ---- errors ----------------------------------------------------------
    function setError(field, msg) {
      var el = card.querySelector('[data-error-for="' + field + '"]');
      if (!el) return;
      if (msg) {
        el.textContent = msg;
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    }

    function clearErrors() {
      var all = card.querySelectorAll('[data-error-for]');
      for (var i = 0; i < all.length; i++) {
        all[i].textContent = '';
        all[i].hidden = true;
      }
    }

    // ---- meter -----------------------------------------------------------
    function paintMeter() {
      if (!canOffer || !els.amount) return;
      var digits = digitsOnly(els.amount.value);
      var amount = digits ? parseInt(digits, 10) : 0;

      var active = 0;
      if (amount > 0) active = segmentOf(bandOf(amount, mid, high));

      for (var i = 0; i < els.segs.length; i++) {
        var n = parseInt(els.segs[i].dataset.seg, 10);
        els.segs[i].classList.toggle('is-active', n === active);
        els.segs[i].classList.toggle('is-dim', active !== 0 && n !== active);
      }
      for (var j = 0; j < els.segLabels.length; j++) {
        var m = parseInt(els.segLabels[j].dataset.segLabel, 10);
        els.segLabels[j].classList.toggle('is-active', m === active);
      }

      var pct = markerPercent(amount, lo, mid, high, hi);
      if (pct === null) {
        els.marker.hidden = true;
        els.calloutTitle.textContent = '';
        els.calloutBody.textContent = EMPTY_BODY;
        card.classList.remove('is-long_shot', 'is-middle', 'is-solid');
        return;
      }
      els.marker.hidden = false;
      els.marker.style.left = pct + '%';

      var band = bandOf(amount, mid, high);
      els.calloutTitle.textContent = COPY[band].title;
      els.calloutBody.textContent = COPY[band].body;
      card.classList.remove('is-long_shot', 'is-middle', 'is-solid');
      card.classList.add('is-' + band);
    }

    // ---- steps -----------------------------------------------------------
    /**
     * Show step `n`.
     *
     * `moveFocus` defaults to true: when someone taps Continue or Back, focus
     * should follow them to the step they asked for. It is passed false for
     * the FIRST render, which is not navigation - nobody asked to go
     * anywhere, the page merely loaded.
     *
     * Focusing on load was landing iOS Safari halfway down the listing page.
     * The card sits about 1100px down, so focusing its amount input scrolled
     * the page to it, stopping 8rem short because of the card's own
     * scroll-margin-top - which put the specs table at the top of the screen
     * and the card just below the fold. `preventScroll: true` was already
     * passed and is honoured by Chromium, which is why it never reproduced
     * anywhere but a phone. The fix is not to focus at all on load, rather
     * than to lean harder on a hint one engine ignores.
     */
    function showStep(n, moveFocus) {
      if (moveFocus === undefined) moveFocus = true;
      state.step = n;
      for (var i = 0; i < els.steps.length; i++) {
        var s = els.steps[i];
        s.hidden = parseInt(s.dataset.step, 10) !== n;
      }
      if (els.progress) {
        var items = els.progress.querySelectorAll('[data-progress-for]');
        for (var j = 0; j < items.length; j++) {
          var pn = parseInt(items[j].dataset.progressFor, 10);
          // Step 1 is not part of the journey in question mode.
          items[j].hidden = state.mode === 'question' && pn === 1;
          items[j].classList.toggle('is-current', pn === n);
          items[j].classList.toggle('is-done', pn < n);
        }
      }
      if (n === 2) renderTurnstile();
      if (n === 3) buildSummary();
      var heading = card.querySelector('[data-step="' + n + '"]');
      if (heading) {
        var focusable = heading.querySelector('input, textarea, button');
        if (moveFocus && focusable && n !== 3) focusable.focus({ preventScroll: true });
      }
    }

    function submitLabelEl() {
      return els.submitLabel || els.submit;
    }

    /** `moveFocus` is forwarded to showStep; see there for why load differs. */
    function setMode(mode, moveFocus) {
      state.mode = mode;
      card.dataset.mode = mode;
      for (var i = 0; i < els.tabs.length; i++) {
        var on = els.tabs[i].dataset.offerMode === mode;
        els.tabs[i].classList.toggle('is-active', on);
        els.tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
      }
      if (els.consentRow) els.consentRow.hidden = mode !== 'offer';
      // The two optional questions are about buying, so a question mode has
      // no use for them. Hidden, and their values are not read when hidden.
      if (els.choices) els.choices.hidden = mode !== 'offer';
      // Phone is required on an offer and optional on a question.
      if (els.phoneOptional) els.phoneOptional.hidden = mode === 'offer';
      if (els.phone) {
        if (mode === 'offer') els.phone.setAttribute('required', '');
        else els.phone.removeAttribute('required');
      }
      if (els.messageOptional) {
        els.messageOptional.textContent = mode === 'question' ? '(required)' : '(optional)';
      }
      if (els.submit) {
        submitLabelEl().textContent =
          mode === 'offer' ? 'Submit Offer for Review' : 'Send Question';
      }
      clearErrors();
      showStep(mode === 'question' ? 2 : 1, moveFocus);
    }

    // ---- validation ------------------------------------------------------
    function validateStep(n) {
      clearErrors();
      var ok = true;

      if (n === 1) {
        var digits = digitsOnly(els.amount.value);
        var amount = digits ? parseInt(digits, 10) : 0;
        if (!digits || amount < 1) {
          setError('amount', 'Enter an amount.');
          ok = false;
        } else if (amount > 10000000) {
          setError('amount', 'That amount is too large.');
          ok = false;
        }
      }

      if (n === 2) {
        if (!els.name.value.trim()) {
          setError('name', 'Please add your name.');
          ok = false;
        }
        if (!emailLooksValid(els.email.value.trim())) {
          setError('email', 'Please check your email address.');
          ok = false;
        }
        if (state.mode === 'offer' && !els.phone.value.trim()) {
          setError('phone', 'Please enter a phone number.');
          ok = false;
        } else if (els.phone.value.trim().length > 30) {
          setError('phone', 'That phone number is too long.');
          ok = false;
        }
        if (state.mode === 'question' && !els.message.value.trim()) {
          setError('message', 'Please write your question.');
          ok = false;
        }
        if (state.mode === 'offer' && els.consent && !els.consent.checked) {
          setError('consent', 'Please confirm you understand this is non-binding.');
          ok = false;
        }
        if (!state.token) {
          setStatus('Please complete the verification check to continue.');
          ok = false;
        }
      }
      return ok;
    }

    // ---- summary ---------------------------------------------------------
    function row(dt, dd) {
      return '<dt>' + dt + '</dt><dd>' + dd + '</dd>';
    }

    /**
     * The checked value of a radio group, or '' when the neutral option is
     * selected. Both are the same thing to everything downstream: skipped.
     */
    function chosen(nodes) {
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].checked) return nodes[i].value;
      }
      return '';
    }

    function escapeHtml(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    function buildSummary() {
      if (!els.summary) return;
      var html = '';
      if (state.mode === 'offer') {
        html += row('Your offer', '$' + withCommas(digitsOnly(els.amount.value)));
      }
      html += row('Listing', escapeHtml(card.dataset.title));
      html += row('Name', escapeHtml(els.name.value.trim()));
      html += row('Email', escapeHtml(els.email.value.trim()));
      if (els.phone.value.trim()) html += row('Phone', escapeHtml(els.phone.value.trim()));
      // Shown only when answered: "Not sure yet" is a skip, not an answer.
      if (state.mode === 'offer') {
        var pay = chosen(els.payment);
        var del = chosen(els.delivery);
        if (pay) html += row('Paying with', pay === 'finance' ? 'Financing' : 'Cash');
        if (del) html += row('Pickup or ship', del === 'ship' ? 'Ship it' : "I'll pick it up");
      }
      if (els.message.value.trim()) html += row('Message', escapeHtml(els.message.value.trim()));
      els.summary.innerHTML = html;
    }

    // ---- turnstile -------------------------------------------------------
    /*
     * Continue on step 2 is gated on the token.
     *
     * Previously Continue only checked the text fields, so you could reach
     * step 3 before verification had finished and then be bounced back by a
     * message about completing a check you could not see. Now the gate is
     * where the wait actually is.
     */
    function setVerifyingState(verifying) {
      var next = card.querySelector('[data-step="2"] [data-offer-next]');
      if (!next) return;
      var label = next.querySelector('.btn__text') || next;
      if (verifying) {
        next.disabled = true;
        next.setAttribute('aria-busy', 'true');
        label.textContent = 'Verifying\u2026';
      } else {
        next.disabled = false;
        next.removeAttribute('aria-busy');
        label.textContent = 'Continue';
      }
      if (els.verifyNote) els.verifyNote.hidden = !verifying;
    }

    function renderTurnstile() {
      if (!window.turnstile || !els.turnstile || !siteKey) return;
      // Rendered once and never torn down: re-rendering on a step change is
      // what would throw away a token that had already been earned.
      if (state.widgetId !== null || state.turnstileBroken) return;

      setVerifyingState(true);
      try {
        state.widgetId = window.turnstile.render(els.turnstile, {
          sitekey: siteKey,
          theme: 'light',
          size: 'normal',
          appearance: 'always',
          callback: function (token) {
            state.token = token || '';
            if (els.tokenInput) els.tokenInput.value = state.token;
            setStatus('');
            setVerifyingState(false);
          },
          'expired-callback': function () {
            state.token = '';
            if (els.tokenInput) els.tokenInput.value = '';
            setVerifyingState(true);
            console.error('[offer-card] Turnstile token expired; resetting');
            resetTurnstile();
          },
          'timeout-callback': function () {
            state.token = '';
            if (els.tokenInput) els.tokenInput.value = '';
            setVerifyingState(true);
            /*
             * At most two retries. reset() starts the challenge again, so an
             * unconditional reset here turns one timeout into an endless
             * timeout/reset cycle - which is exactly what it did: 165 console
             * errors and a spinning widget on a page that looked idle.
             */
            if (state.resets >= 2) {
              state.turnstileBroken = true;
              setStatus(
                "The verification check keeps timing out, so this form can't be sent " +
                  'right now. Please reload the page, or email sales@usefuljunk.com and ' +
                  'mention ' + slot + '.',
                'Turnstile timed out ' + (state.resets + 1) + ' times; giving up'
              );
              return;
            }
            state.resets += 1;
            console.error('[offer-card] Turnstile timed out; reset ' + state.resets + ' of 2');
            resetTurnstile();
          },
          'error-callback': function (code) {
            state.turnstileBroken = true;
            setVerifyingState(true);
            setStatus(
              'The verification check could not load (error ' + code + '), so this form ' +
                "can't be sent right now. Please email sales@usefuljunk.com and mention " +
                slot + '.',
              'Turnstile error-callback code: ' + code + ' on ' + location.hostname
            );
          }
        });
      } catch (err) {
        state.turnstileBroken = true;
        setStatus(
          "The verification check could not load, so this form can't be sent right now. " +
            'Please email sales@usefuljunk.com and mention ' + slot + '.',
          err
        );
      }

      /*
       * Backstop for a widget that neither succeeds nor calls an error
       * callback - an invisible widget that quietly never resolves leaves the
       * visitor staring at a disabled button with no explanation otherwise.
       */
      setTimeout(function () {
        if (!state.token && !state.turnstileBroken) {
          setStatus(
            'The verification check is taking longer than expected. Reload the page and try ' +
              'again, or email sales@usefuljunk.com and mention ' + slot + '.',
            'no Turnstile token after 25s; widgetId=' + state.widgetId
          );
        }
      }, 25000);
    }

    function resetTurnstile() {
      state.token = '';
      if (els.tokenInput) els.tokenInput.value = '';
      if (window.turnstile && state.widgetId !== null) {
        window.turnstile.reset(state.widgetId);
        setVerifyingState(true);
      }
    }

    // Read the captured token, not getResponse(). See state.token above.
    function turnstileToken() {
      return state.token || '';
    }

    /*
     * The script is async, so wait for the global to appear - but only wait.
     * Rendering is driven by showStep(2). The old loop retried render() itself,
     * so a sitekey rejection produced a hundred console errors instead of one.
     */
    var tries = 0;
    var waitForTurnstile = setInterval(function () {
      if (window.turnstile) {
        clearInterval(waitForTurnstile);
        if (state.step === 2) renderTurnstile();
      } else if (++tries > 40) {
        clearInterval(waitForTurnstile);
        console.error('[offer-card] Turnstile script never loaded');
      }
    }, 250);

    // ---- submit ----------------------------------------------------------
    function submit() {
      if (state.sending) return;
      if (!validateStep(2)) {
        showStep(2);
        return;
      }

      var token = turnstileToken();
      if (!token) {
        // Two different situations, two different messages: the widget never
        // loaded (nothing the visitor can do) versus it loaded but was not
        // completed (they can).
        if (state.turnstileBroken || !window.turnstile || state.widgetId === null) {
          setStatus(
            "The verification check could not load, so this form can't be sent right now. " +
              'Please email sales@usefuljunk.com and mention ' + slot + '.',
            'no Turnstile widget: broken=' + state.turnstileBroken +
              ' global=' + typeof window.turnstile + ' widgetId=' + state.widgetId
          );
        } else {
          setStatus('Please complete the verification check to continue.');
          showStep(2);
        }
        return;
      }

      state.sending = true;
      els.submit.disabled = true;
      var labelEl = submitLabelEl();
      els.submit.dataset.label = labelEl.textContent;
      labelEl.textContent = 'Sending...';
      setStatus('');

      var payload = {
        slot_id: slot,
        kind: state.mode,
        amount: state.mode === 'offer' ? parseInt(digitsOnly(els.amount.value), 10) : null,
        name: els.name.value.trim(),
        email: els.email.value.trim(),
        phone: els.phone.value.trim(),
        message: els.message.value.trim(),
        // '' when skipped. The Worker treats absent and '' identically and
        // omits the Shopify field rather than writing an empty choice.
        payment_method: state.mode === 'offer' ? chosen(els.payment) : '',
        delivery_method: state.mode === 'offer' ? chosen(els.delivery) : '',
        turnstile_token: token,
        website: els.hp ? els.hp.value : ''
      };

      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          // Keep the status around: a 403 and a 500 are the same to the
          // visitor but not to whoever reads the console.
          return res.json()
            .catch(function () {
              return {};
            })
            .then(function (body) {
              return { status: res.status, ok: res.ok, body: body };
            });
        })
        .then(function (res) {
          var data = res.body || {};
          if (!res.ok) {
            throw new Error('Worker returned HTTP ' + res.status + ': ' + JSON.stringify(data));
          }
          if (data && data.ok && data.reference) {
            form.hidden = true;
            if (els.progress) els.progress.hidden = true;
            var toggle = card.querySelector('.offer-card__toggle');
            if (toggle) toggle.hidden = true;
            els.reference.textContent = data.reference;
            /*
             * `updated` comes from the Worker and is true when this revised an
             * existing offer. Saying "we've got your offer" to someone who just
             * changed their number reads as though the change was ignored.
             */
            els.doneTitle.textContent = data.updated
              ? "Thanks - we've updated your offer."
              : state.mode === 'offer'
                ? "Thanks - we've got your offer."
                : "Thanks - we've got your question.";
            els.done.hidden = false;
            els.done.setAttribute('tabindex', '-1');
            els.done.focus({ preventScroll: true });
            card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            throw new Error('Worker replied 2xx without ok/reference: ' + JSON.stringify(data));
          }
        })
        .catch(function (err) {
          // Covers all three: network failure (fetch rejects), non-2xx, and a
          // 2xx body that is not a success. None of them may pass silently.
          setStatus(
            "Sorry, that didn't go through. Please check your details and try again, " +
              'or email sales@usefuljunk.com and mention ' + slot + '.',
            err
          );
          // A used token cannot be replayed, so the visitor needs a fresh one.
          resetTurnstile();
        })
        .then(function () {
          state.sending = false;
          els.submit.disabled = false;
          if (els.submit.dataset.label) submitLabelEl().textContent = els.submit.dataset.label;
        });
    }

    // ---- wiring ----------------------------------------------------------
    for (var t = 0; t < els.tabs.length; t++) {
      els.tabs[t].addEventListener('click', function (e) {
        // A tab tap IS navigation, so focus follows.
        setMode(e.currentTarget.dataset.offerMode, true);
      });
    }

    if (els.amount) {
      els.amount.addEventListener('input', function () {
        var caretFromEnd = this.value.length - this.selectionStart;
        var digits = digitsOnly(this.value).slice(0, 8);
        this.value = digits ? withCommas(digits) : '';
        var pos = Math.max(0, this.value.length - caretFromEnd);
        try {
          this.setSelectionRange(pos, pos);
        } catch (err) {
          /* older browsers on a text input mid-composition */
        }
        paintMeter();
      });
    }

    card.addEventListener('click', function (e) {
      var next = e.target.closest('[data-offer-next]');
      if (next) {
        if (validateStep(state.step)) showStep(state.step === 1 ? 2 : 3);
        return;
      }
      var back = e.target.closest('[data-offer-back]');
      if (back) {
        clearErrors();
        // Leave the status alone: if verification is still pending, that is
        // exactly the message the visitor needs on the way back.
        showStep(state.step === 3 ? 2 : 1);
      }
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit();
    });

    // First render: no focus. See showStep.
    setMode(state.mode, false);
    paintMeter();
  }

  function boot() {
    var cards = document.querySelectorAll('[data-offer-card]');
    for (var i = 0; i < cards.length; i++) init(cards[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
